import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Map from './Map';
import ScorePanel from './ScorePanel';
import Sidebar from '../Layout/Sidebar';
import { gameAPI } from '../../services/api';
import { GameMode, GameState, SCORING, State, Difficulty, getGeographyType } from '../../types';
import './Game.css';

const GameBoard: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const gameMode = (searchParams.get('mode') || 'full_usa') as GameMode;
  const difficulty = (searchParams.get('difficulty') || 'hard') as Difficulty;

  const [gameStates, setGameStates] = useState<GameState[]>([]);
  const [allStates, setAllStates] = useState<State[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [completedStates, setCompletedStates] = useState<Set<string>>(new Set());
  const [incorrectStates, setIncorrectStates] = useState<Set<string>>(new Set());
  const [incorrectState, setIncorrectState] = useState<string | null>(null);
  const [totalScore, setTotalScore] = useState(0);
  const [startTime, setStartTime] = useState(Date.now());
  const [elapsedTime, setElapsedTime] = useState(0);
  const [timerPaused, setTimerPaused] = useState(false);
  const [pauseStartTime, setPauseStartTime] = useState<number | null>(null);
  const [totalPausedTime, setTotalPausedTime] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showStateInfo, setShowStateInfo] = useState(false);
  const [currentStateInfo, setCurrentStateInfo] = useState<State | null>(null);
  const [rightSidebarOpen, setRightSidebarOpen] = useState(true);

  useEffect(() => {
    initializeGame();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameMode, difficulty]);

  useEffect(() => {
    // Update elapsed time every second (only when not paused)
    if (timerPaused) return;

    const timer = setInterval(() => {
      const rawElapsed = Math.floor((Date.now() - startTime) / 1000);
      setElapsedTime(rawElapsed - Math.floor(totalPausedTime / 1000));
    }, 1000);

    return () => clearInterval(timer);
  }, [startTime, timerPaused, totalPausedTime]);

  const initializeGame = async () => {
    try {
      setLoading(true);

      // Determine geography type from game mode
      const geographyType = getGeographyType(gameMode);

      // Load entities for this geography type
      const statesData = await gameAPI.getAllStates(geographyType);

      // Filter by region if not full_usa
      let filteredStates = statesData.states;
      if (gameMode !== 'full_usa') {
        // Region mappings for US states
        const US_REGIONS: { [key: string]: string[] } = {
          northeast: ['ME', 'NH', 'VT', 'MA', 'RI', 'CT', 'NY', 'NJ', 'PA'],
          mid_atlantic: ['DE', 'MD', 'VA', 'WV', 'DC'],
          southeast: ['NC', 'SC', 'GA', 'FL', 'AL', 'MS', 'TN', 'KY', 'AR', 'LA'],
          midwest: ['OH', 'IN', 'IL', 'MI', 'WI', 'MN', 'IA', 'MO', 'ND', 'SD', 'NE', 'KS'],
          southwest: ['TX', 'OK', 'NM', 'AZ'],
          west: ['CA', 'NV', 'UT', 'CO', 'WY', 'MT', 'ID', 'OR', 'WA', 'AK', 'HI']
        };

        // Region mappings for countries
        const COUNTRY_REGIONS: { [key: string]: string[] } = {
          north_america: ['AG', 'GD', 'GT', 'HN', 'HT', 'JM', 'KN', 'BS', 'BZ', 'BB', 'DM', 'US', 'MX', 'CA', 'CR', 'CU', 'DO', 'LC', 'NI', 'PA', 'SV', 'TT', 'VC'],
          south_america: ['GY', 'BO', 'AR', 'BR', 'CL', 'CO', 'EC', 'PE', 'PY', 'SR', 'UY', 'VE'],
          europe: ['AL', 'GR', 'HR', 'HU', 'IE', 'IS', 'XK', 'AT', 'BE', 'BG', 'BA', 'BY', 'GB', 'FR', 'CY', 'CZ', 'IT', 'DE', 'ES', 'CH', 'DK', 'EE', 'FI', 'LT', 'LU', 'LV', 'MD', 'MK', 'ME', 'NL', 'NO', 'PL', 'PT', 'RO', 'RU', 'RS', 'SK', 'SI', 'SE', 'UA'],
          africa: ['AO', 'GH', 'GN', 'GM', 'GW', 'GQ', 'KE', 'LR', 'BI', 'BJ', 'BF', 'BW', 'CF', 'LY', 'DJ', 'CI', 'CM', 'CD', 'CG', 'KM', 'CV', 'DZ', 'EG', 'ER', 'ET', 'GA', 'LS', 'MA', 'MG', 'ML', 'MZ', 'MR', 'MU', 'MW', 'NA', 'NE', 'NG', 'RW', 'SD', 'SN', 'SL', 'SO', 'SS', 'ST', 'SZ', 'TD', 'TG', 'TN', 'TZ', 'UG', 'ZA', 'ZM', 'ZW'],
          asia: ['AF', 'AE', 'AM', 'ID', 'IR', 'IQ', 'IL', 'JO', 'KZ', 'KG', 'KH', 'KR', 'KW', 'LA', 'LB', 'AZ', 'BD', 'BN', 'BT', 'IN', 'JP', 'CN', 'LK', 'MM', 'MN', 'MY', 'NP', 'OM', 'PK', 'PH', 'KP', 'QA', 'SA', 'SY', 'TH', 'TJ', 'TM', 'TR', 'TW', 'UZ', 'VN', 'YE']
        };

        const regionAbbreviations = geographyType === 'country'
          ? COUNTRY_REGIONS[gameMode]
          : US_REGIONS[gameMode];

        if (regionAbbreviations) {
          filteredStates = statesData.states.filter((s: State) =>
            regionAbbreviations.includes(s.abbreviation)
          );
        }
      }

      // Shuffle the states to randomize order each game
      filteredStates = filteredStates.sort(() => Math.random() - 0.5);

      // Generate clues based on difficulty
      const entityType = geographyType === 'country' ? 'Country' : 'State';
      const mappedStates = filteredStates.map((s: State) => {
        let clue_1: string;
        let clue_2: string;
        let clue_3: string;

        if (difficulty === 'easy') {
          clue_1 = `${entityType}: ${s.name}`;
          clue_2 = `Capital: ${s.capital}`;
          clue_3 = s.clue_1 || 'No clue available';
        } else if (difficulty === 'medium') {
          clue_1 = `Capital: ${s.capital}`;
          clue_2 = s.clue_2 || s.clue_1 || 'No clue available';
          clue_3 = s.clue_3 || s.clue_2 || 'No clue available';
        } else {
          clue_1 = s.clue_1 || 'No clue available';
          clue_2 = s.clue_2 || 'No clue available';
          clue_3 = s.clue_3 || 'No clue available';
        }

        return {
          ...s,
          clue_1,
          clue_2,
          clue_3,
          currentClue: clue_1,
          clueLevel: 1,
          attempts: 0,
          hintUsed: false,
          completed: false,
          score: 0
        };
      });

      setGameStates(mappedStates);
      setAllStates(statesData.states);
      setStartTime(Date.now());
      setTotalScore(0);
      setCurrentIndex(0);
      setCompletedStates(new Set());
    } catch (error: any) {
      console.error('Error initializing game:', error);
      toast.error(`Failed to start game: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleStateClick = async (clickedAbbr: string) => {
    if (loading || currentIndex >= gameStates.length) return;

    const currentState = gameStates[currentIndex];
    const isCorrect = currentState.abbreviation === clickedAbbr;

    if (isCorrect) {
      await handleCorrectAnswer(currentState);
    } else {
      await handleIncorrectAnswer(currentState, clickedAbbr);
    }
  };

  const handleCorrectAnswer = async (state: GameState) => {
    // Calculate score
    let points = 0;
    if (state.attempts === 0) {
      points = state.hintUsed ? SCORING.CORRECT_FIRST_WITH_HINT : SCORING.CORRECT_FIRST_NO_HINT;
    } else if (state.attempts === 1) {
      points = SCORING.CORRECT_SECOND;
    }

    // Update state
    const updatedStates = [...gameStates];
    updatedStates[currentIndex] = {
      ...state,
      completed: true,
      score: points
    };
    setGameStates(updatedStates);

    // Add to completed
    setCompletedStates(prev => new Set(prev).add(state.abbreviation));
    setTotalScore(prev => prev + points);

    toast.success(`Correct! ${state.name} - ${state.capital} (+${points} points)`, {
      autoClose: 2000
    });

    // Pause timer and show state info modal
    setTimerPaused(true);
    setPauseStartTime(Date.now());
    try {
      const stateData = await gameAPI.getStateById(state.id);
      setCurrentStateInfo(stateData.state);
      setShowStateInfo(true);
    } catch (error) {
      console.error('Error fetching state info:', error);
      setTimerPaused(false);
      setPauseStartTime(null);
      moveToNextState();
    }
  };

  const handleIncorrectAnswer = async (state: GameState, clickedAbbr: string) => {
    // Flash red
    setIncorrectState(clickedAbbr);
    setTimeout(() => setIncorrectState(null), 500);

    const updatedState = { ...state, attempts: state.attempts + 1 };

    // Update the gameStates array immediately with the new attempt count
    const updatedStates = [...gameStates];
    updatedStates[currentIndex] = updatedState;
    setGameStates(updatedStates);

    if (updatedState.attempts >= 2) {
      // Failed - reveal answer
      toast.error(`The correct answer was ${state.name}`, {
        autoClose: 3000
      });

      // Update to mark as completed with 0 score
      updatedStates[currentIndex] = { ...updatedState, completed: true, score: 0 };
      setGameStates(updatedStates);

      setCompletedStates(prev => new Set(prev).add(state.abbreviation));
      setIncorrectStates(prev => new Set(prev).add(state.abbreviation));

      // Pause timer and show state info
      setTimerPaused(true);
      setPauseStartTime(Date.now());
      try {
        const stateData = await gameAPI.getStateById(state.id);
        setCurrentStateInfo(stateData.state);
        setShowStateInfo(true);
      } catch (error) {
        console.error('Error fetching state info:', error);
        setTimerPaused(false);
        setPauseStartTime(null);
        moveToNextState();
      }
    } else {
      // Give next clue
      const nextClueLevel = updatedState.clueLevel + 1;
      const nextClueKey = `clue_${nextClueLevel}` as keyof GameState;
      const nextClue = updatedState[nextClueKey] as string;

      if (nextClue) {
        updatedStates[currentIndex] = {
          ...updatedState,
          currentClue: nextClue,
          clueLevel: nextClueLevel
        };
        setGameStates(updatedStates);
        toast.info('Try again with a new clue!');
      }
    }
  };

  const moveToNextState = () => {
    // Track paused time before resuming
    if (pauseStartTime) {
      const pauseDuration = Date.now() - pauseStartTime;
      setTotalPausedTime(prev => prev + pauseDuration);
      setPauseStartTime(null);
    }

    setShowStateInfo(false);
    setCurrentStateInfo(null);
    setTimerPaused(false);

    if (currentIndex < gameStates.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      // Game complete
      completeGame();
    }
  };

  const handleRequestHint = () => {
    const currentState = gameStates[currentIndex];

    if (currentState.hintUsed || currentState.clueLevel >= 3 || currentState.attempts > 0) {
      return;
    }

    const nextClueLevel = currentState.clueLevel + 1;
    const nextClueKey = `clue_${nextClueLevel}` as keyof GameState;
    const nextClue = currentState[nextClueKey] as string;

    if (!nextClue) {
      toast.error('No more hints available');
      return;
    }

    const updatedStates = [...gameStates];
    updatedStates[currentIndex] = {
      ...currentState,
      currentClue: nextClue,
      clueLevel: nextClueLevel,
      hintUsed: true
    };
    setGameStates(updatedStates);

    toast.info('Here\'s an easier clue!');
  };

  const completeGame = () => {
    // Calculate time without paused duration
    const rawTime = Math.floor((Date.now() - startTime) / 1000);
    const timeSeconds = rawTime - Math.floor(totalPausedTime / 1000);

    // Get list of missed states (states with score of 0)
    const missedStates = gameStates
      .filter(state => state.score === 0)
      .map(state => state.name);

    // Store missed states in sessionStorage for results page
    if (missedStates.length > 0) {
      sessionStorage.setItem('missedStates', JSON.stringify(missedStates));
    }

    toast.success(`Game complete! Final score: ${totalScore}`, {
      autoClose: 3000
    });

    // Navigate to results
    setTimeout(() => {
      navigate(`/results?score=${totalScore}&time=${timeSeconds}&mode=${gameMode}&difficulty=${difficulty}`);
    }, 2000);
  };

  if (loading) {
    return (
      <div className="game-loading">
        <div className="spinner"></div>
        <p>Loading game...</p>
      </div>
    );
  }

  const currentState = gameStates[currentIndex];

  return (
    <div className="game-container">
      <main className="game-main">
        {/* Clue Display - Above the map for mobile-friendly layout */}
        {currentState && (
          <div className="clue-header">
            <div className="clue-header-content">
              <div className="clue-level-indicator">
                <span className="clue-badge">Clue {currentState.clueLevel}</span>
                <span className="progress-badge">
                  {currentIndex + 1} of {gameStates.length}
                </span>
              </div>
              <div className="clue-text-main">
                {currentState.currentClue}
              </div>
              <div className="clue-actions">
                <button
                  className="hint-button-inline"
                  onClick={handleRequestHint}
                  disabled={currentState.hintUsed || currentState.attempts > 0 || currentState.clueLevel >= 3}
                >
                  Get Hint
                </button>
                <div className="score-inline">
                  Score: <strong>{totalScore}</strong> | Time: <strong>{Math.floor(elapsedTime / 60)}:{(elapsedTime % 60).toString().padStart(2, '0')}</strong>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Map */}
        <div className="map-section">
          <Map
            onStateClick={handleStateClick}
            completedStates={completedStates}
            incorrectStates={incorrectStates}
            incorrectState={incorrectState}
            allStates={allStates}
            gameMode={gameMode}
          />
        </div>

        {/* Stats button for desktop - opens sidebar */}
        <button
          className="stats-toggle-btn"
          onClick={() => setRightSidebarOpen(!rightSidebarOpen)}
          aria-label="Toggle detailed stats"
        >
          Stats
        </button>
      </main>

      {/* Right Sidebar - Detailed stats (optional on mobile) */}
      <Sidebar position="right" isOpen={rightSidebarOpen} onClose={() => setRightSidebarOpen(false)}>
        <ScorePanel
          gameMode={gameMode}
          currentScore={totalScore}
          elapsedTime={elapsedTime}
        />
      </Sidebar>

      {/* State Info Modal */}
      {showStateInfo && currentStateInfo && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{currentStateInfo.name} {currentStateInfo.flag_emoji}</h2>
            <div className="state-info-grid">
              <div className="info-item">
                <strong>Capital:</strong> {currentStateInfo.capital}
              </div>
              {currentStateInfo.nickname && (
                <div className="info-item">
                  <strong>Nickname:</strong> {currentStateInfo.nickname}
                </div>
              )}
              {currentStateInfo.population && currentStateInfo.population > 0 && (
                <div className="info-item">
                  <strong>Population:</strong> {currentStateInfo.population.toLocaleString()}
                </div>
              )}
              <div className="info-item">
                <strong>Region:</strong> {currentStateInfo.region}
              </div>
              {currentStateInfo.largest_city && (
                <div className="info-item">
                  <strong>Largest City:</strong> {currentStateInfo.largest_city}
                </div>
              )}
            </div>
            <button className="modal-close-btn" onClick={moveToNextState}>
              Next {currentIndex < gameStates.length - 1 ? '→' : '(Finish)'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameBoard;
